import { useState } from 'react'
import { apiDownload } from '../../api/client'
import { userFacingMessage } from '../../api/errors'
import { useBillingStatus, type BillingStatus } from '../../api/queries/billing'
import { PurchaseActions } from '../billing/PurchaseActions'
import { describeAnalysesLeft, formatDate, formatMonthly } from '../billing/pricing'
import billingStyles from '../billing/Billing.module.css'
import { useDeleteAccount } from '../../api/queries/profile'
import { useAuth } from '../../app/providers/AuthProvider'
import { Button } from '../../ui/Button'
import { ConfirmDialog } from '../../ui/ConfirmDialog'
import { ReadOnlyRow, SettingsCard } from './SettingsCard'
import styles from './Settings.module.css'

/**
 * Plan and usage.
 *
 * The figures are the backend's, per ledger bucket; the frontend adds them up
 * only to print a total next to its parts. The purchase controls come from
 * `PurchaseActions`, which shows each one only when the backend says it would
 * work — the fake-control rule (Mike, 2026-08-22) applied to money.
 */
export function PlanCard() {
  const billing = useBillingStatus()

  if (billing.isPending) return <SettingsCard title="Plan & usage" description="Loading…" editable={false}><span /></SettingsCard>
  if (billing.isError) {
    return (
      <SettingsCard title="Plan & usage" description="Your current plan and remaining analyses." editable={false}>
        <p className={styles.error} role="alert">{userFacingMessage(billing.error)}</p>
      </SettingsCard>
    )
  }

  const status = billing.data
  const { subscription, usage, unmetered, pricing } = status
  const period = subscription.current_period_end ? formatDate(subscription.current_period_end) : null
  const active = subscription.status === 'active'
  // A subscription in arrears is still running: the grace period exists precisely
  // so the month's quota stays spendable while the card is fixed. Reporting what
  // has been used against it is therefore still true, and hiding it leaves someone
  // mid-month unable to see how much of the allowance is gone. Renewal is the
  // opposite case and stays hidden — see the comment on that row.
  const planLabel =
    subscription.plan_code === 'solo' ? `${subscription.plan_name} · ${formatMonthly(pricing.solo_monthly_price_cents, pricing.currency)}` : subscription.plan_name

  return (
    <SettingsCard
      title="Plan & usage"
      description="Your current plan and remaining analyses."
      summary={[subscription.plan_name, unmetered ? 'Unlimited analyses' : describeAnalysesLeft(usage)]}
      editable={false}
    >
      <PaymentProblem status={status} />
      <ReadOnlyRow label="Plan" value={planLabel} />
      {(active || subscription.status === 'past_due') && !unmetered ? (
        <ReadOnlyRow label="This month" value={`${usage.monthly_analyses_used} of ${usage.included_monthly_analyses} used`} />
      ) : null}
      <ReadOnlyRow label="Analyses left" value={unmetered ? 'Unlimited' : describeAnalysesLeft(usage)} />
      {/* Shown only when there is a period to report — a "Renewal: —" row states
          nothing and invites the question it cannot answer. */}
      {period && (active || subscription.cancel_at_period_end) ? (
        <ReadOnlyRow label="Renewal" value={subscription.cancel_at_period_end ? `Ends ${period}` : `Renews ${period}`} />
      ) : null}
      {/* Credit that will die on a date the user has not been told about is how a
          paying customer becomes an angry one. The period quota's date is the
          Renewal row; this covers granted credit on an account with no period. */}
      {!active && usage.next_expiry_at && usage.granted_credits_available > 0 ? (
        <ReadOnlyRow label="Granted credits expire" value={formatDate(usage.next_expiry_at)} />
      ) : null}
      {!unmetered ? (
        <div className={styles.actions}>
          <PurchaseActions status={status} />
        </div>
      ) : null}
    </SettingsCard>
  )
}

/** A failed payment, said plainly, with the date that matters. */
function PaymentProblem({ status }: { status: BillingStatus }) {
  const { subscription, actions } = status
  if (actions.read_only_reason === 'account_read_only') {
    return <p className={billingStyles.notice} role="status">Your account is read-only. Get in touch and we will sort it out.</p>
  }
  if (subscription.status === 'read_only') {
    return (
      <p className={billingStyles.notice} role="status">
        Your last payment failed and the grace period has ended. Update your card under Manage billing to start analysing again.
      </p>
    )
  }
  if (subscription.status === 'past_due') {
    const until = subscription.grace_period_ends_at ? ` You can keep analysing until ${formatDate(subscription.grace_period_ends_at)}.` : ''
    return (
      <p className={billingStyles.notice} role="status">
        Your last payment failed. Update your card under Manage billing.{until}
      </p>
    )
  }
  return null
}

/**
 * Export, as a link to the endpoint rather than a file built here.
 *
 * The old export was assembled in the browser from the deals it had loaded. The
 * current board loads call history lazily, so that approach would now write
 * blank Call and Decision rows for anything the user had not opened.
 */
export function ExportCard() {
  const [sections, setSections] = useState({ deals: true, calls: true, decisions: true })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chosen = Object.values(sections).some(Boolean)

  async function download() {
    setBusy(true)
    setError(null)
    try {
      // Through the API client so the request carries the session; a plain
      // anchor href would arrive unauthenticated.
      const query = new URLSearchParams(
        Object.entries(sections).map(([k, v]) => [k, String(v)]),
      ).toString()
      const { blob, filename } = await apiDownload(`/export?${query}`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      setError(userFacingMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard
      title="Export data"
      description="Download your data as a CSV file. Choose what to include."
      summary={['Deals, calls, decisions']}
      editable={false}
    >
      <div className={styles.checks}>
        {([
          ['deals', 'Deals'],
          ['calls', 'Calls'],
          ['decisions', 'Decisions'],
        ] as const).map(([key, label]) => (
          <label key={key} className={styles.check}>
            <input
              type="checkbox"
              checked={sections[key]}
              onChange={(e) => setSections((s) => ({ ...s, [key]: e.target.checked }))}
            />
            {label}
          </label>
        ))}
      </div>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.actions}>
        <Button onClick={() => void download()} disabled={!chosen || busy}>
          {busy ? 'Preparing…' : 'Export CSV'}
        </Button>
      </div>
    </SettingsCard>
  )
}

/**
 * Closing the account.
 *
 * The copy states what actually happens (Mike, 2026-08-22): deletion is a soft
 * delete and the data is removed after 30 days. It neither claims the data is
 * gone this instant nor offers a recovery it cannot honour — the same honesty
 * the deal-delete confirmation carries.
 */
export function DeleteAccountCard() {
  const [asking, setAsking] = useState(false)
  const { signOut } = useAuth()
  const remove = useDeleteAccount()

  return (
    <>
      <SettingsCard
        title="Delete account"
        description="Closing your account signs you out immediately and removes your deals, calls and analysis history after 30 days."
        summary={['Removed after 30 days']}
        danger
        editable={false}
      >
        {remove.isError ? (
          <p className={styles.error} role="alert">{userFacingMessage(remove.error)}</p>
        ) : null}
        <div className={styles.actions}>
          <Button onClick={() => setAsking(true)} disabled={remove.isPending}>
            Delete account
          </Button>
        </div>
      </SettingsCard>

      {asking ? (
        <ConfirmDialog
          title="Delete account"
          body="Your account is closed straight away and you are signed out. Your deals, calls and analysis history are removed 30 days later."
          confirmLabel="Delete account"
          pending={remove.isPending}
          onCancel={() => setAsking(false)}
          onConfirm={() => remove.mutate(undefined, { onSuccess: () => signOut() })}
        />
      ) : null}
    </>
  )
}
